import { ApplicationRef, inject, Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { EMPTY, filter, first, interval, merge, of, timer } from "rxjs";
import { exhaustMap, map, startWith, switchMap, take, takeUntil, tap, timeout } from "rxjs/operators";
import { PCP_LOGGER } from '@pcp/logging';
import { AuthApiService } from "../services/auth-api.service";
import { PcpAuthService } from "../services/pcp-auth.service";
import { authServiceActions } from "./auth-service.actions";
import { LoginErrorNotificationService } from "../services/login-error-notification.service";
import { AuthBroadcastChannelService } from "../services/auth-broadcast-channel.service";
import { TimeSkewService } from "../services/time-skew.service";


// Fallback interval for critical period (last 5 minutes before token expiry)
const FALLBACK_CHECK_INTERVAL_MS = 30 * 1000; // 30 seconds
const FALLBACK_CRITICAL_PERIOD_MS = 5 * 60 * 1000; // 5 minutes


@Injectable()
export class AuthEffects {

    private readonly appIsStable$ = inject(ApplicationRef).isStable.pipe(
        first((isStable) => isStable),
        timeout({ first: 30_000, with: () => of(true) }),
    );
    private readonly authService = inject(PcpAuthService);
    private readonly timeSkewService = inject(TimeSkewService);
    private readonly log = inject(PCP_LOGGER).createScoped('auth');

    readonly userLogin$ = createEffect(() => this.actions$.pipe(
        ofType(
            authServiceActions.userLoginRequested
        ),
        switchMap(({ authData }) => {
            if (authData.clientType === 'Teacher' && authData.method === 'Serials') {
                return this.authService.loginAsTeacherUsingDongle(authData.credentials.serials);
            }

            if (authData.clientType === 'Teacher' && authData.method === 'UsernamePassword') {
                return this.authService.loginAsTeacherUsingUsernameAndPassword(
                    authData.credentials.username,
                    authData.credentials.password
                );
            }

            if (authData.clientType === 'Beamer' && authData.method === 'BeamerAuthCode') {
                return this.authService.loginAsBeamer(authData.credentials.code);
            }

            if (authData.clientType === 'Student' && authData.method === 'UsernamePassword') {
                // TODO: add student login
                throw new Error('Student login using username+password in not implemented yet');
            }

            // PushTheButton Student
            if (authData.clientType === 'Student' && authData.method === 'StudentEnetToken') {
                return this.authService.loginAsStudentWithLessonCodeAndEnetToken(
                    authData.credentials.lessonCode,
                    authData.credentials.enetToken
                );
            }

            // LiveLesson Student
            if (authData.clientType === 'LiveLessonStudent' && authData.method === 'LiveLessonIdAndLoginToken') {
                return this.authService.loginAsLiveLessonStudent(
                    authData.credentials.liveLessonId,
                    authData.credentials.liveLessonLoginToken
                );
            }

            // LiveLesson Moderator
            if (authData.clientType === 'LiveLessonModerator' && authData.method === 'LiveLessonIdAndLoginToken') {
                return this.authService.loginAsLiveLessonModerator(
                    authData.credentials.liveLessonId,
                    authData.credentials.liveLessonLoginToken
                );
            }

            throw new Error('Invalid auth method or client type');
        }),
    ), { dispatch: false });

    readonly requestTokenRefresh$ = createEffect(() => this.actions$.pipe(
        ofType(authServiceActions.tokenRefreshRequested),
        exhaustMap(() => this.authService.refreshTokenIfRequired().pipe(
            timeout(30000),
        )),
    ), { dispatch: false });

    /**
     * Hybrid token refresh scheduler: precise timer + fallback interval.
     * Uses actual token expiration instead of hardcoded assumptions.
     */
    readonly refreshTokenScheduler$ = createEffect(() => this.actions$.pipe(
        ofType(
            authServiceActions.tokenRestoreSuccess,
            authServiceActions.tokenRefreshSuccess,
            authServiceActions.userLoginSuccess,
        ),
        switchMap(action => this.appIsStable$.pipe(
            switchMap(() => {
                const tokenData = action.tokenData;
                const adjustedNow = this.timeSkewService.getAdjustedNow();
                const expiresAt = tokenData.expiresAt;
                const lifetime = expiresAt.diff(adjustedNow).toMillis();

                if (lifetime <= 0) {
                    this.log.warn('Token already expired - triggering immediate refresh');
                    return this.authService.refreshTokenIfRequired();
                }

                // Refresh at 90% of lifetime, but at least 1 minute before expiry
                const refreshThreshold = Math.max(lifetime * 0.9, lifetime - 60000);
                const timeUntilRefresh = Math.max(0, refreshThreshold);

                this.log.info(`Token refresh scheduled in ${Math.round(timeUntilRefresh / 1000)}s (${Math.round(timeUntilRefresh / 60000)}min)`);

                // Primary strategy: Precise timer
                const primaryRefresh$ = timeUntilRefresh > 0
                    ? timer(timeUntilRefresh).pipe(
                        filter(() => this.authService.isTokenRefreshRequired()),
                        switchMap(() => this.authService.refreshTokenIfRequired()),
                        take(1)
                    )
                    : EMPTY;

                // Fallback strategy: Interval polling in critical period (last 5 minutes)
                const fallbackRefresh$ = (lifetime <= FALLBACK_CRITICAL_PERIOD_MS || timeUntilRefresh <= FALLBACK_CRITICAL_PERIOD_MS)
                    ? interval(FALLBACK_CHECK_INTERVAL_MS).pipe(
                        startWith(0), // Check immediately if we're already in critical period
                        filter(() => this.authService.isTokenRefreshRequired()),
                        switchMap(() => this.authService.refreshTokenIfRequired())
                    )
                    : EMPTY;

                // Combine both strategies
                return merge(primaryRefresh$, fallbackRefresh$);
            })
        )),
        takeUntil(
            this.actions$.pipe(
                ofType(
                    authServiceActions.tokenRestoreFailed,
                    authServiceActions.tokenRefreshFailed,
                    authServiceActions.userLoginFailed,

                    authServiceActions.userHasBeenLoggedOut,
                ),
            )
        ),
    ), { dispatch: false });

    readonly closeDialogsOnLogout$ = createEffect(() => this.actions$.pipe(
        ofType(authServiceActions.userHasBeenLoggedOut),
        tap(() => this.dialog.closeAll()),
    ), { dispatch: false });

    readonly showNotificationOnLoginFailure$ = createEffect(() => this.actions$.pipe(
        ofType(authServiceActions.userLoginFailed),
        switchMap(({ error }) => this.loginErrorService.showLoginErrorNotification(error)),
    ), { dispatch: false });

    readonly logoutAndClearTokensOnRefreshFailure$ = createEffect(() => this.actions$.pipe(
        ofType(authServiceActions.tokenRefreshFailed),
        filter(({ error }) => {
            // Only logout on authentication errors (401/403), not network errors
            if (error?.status === 401 || error?.status === 403) {
                return true;
            }
            this.log.info('Token refresh failed due to network/temporary error - not logging out', { error });
            return false;
        }),
        switchMap(() => this.router.navigateByUrl('/login')),
        map(() => authServiceActions.userHasBeenLoggedOut()),
    ));

    readonly logOutCurrentUser$ = createEffect(() => this.actions$.pipe(
        ofType(authServiceActions.logOutUser),
        switchMap(() => {
            this.authService.logOut();
            return this.router.navigateByUrl('/login');
        }),
    ), { dispatch: false });

    /**
     * Received "logout" message from broadcast channel from another tab -> logout the current user on this tab.
     * Logout the
     */
    readonly onLogoutBroadcastMessageReceived$ = createEffect(() => this.authBroadcastChannel.onMessageReceived$.pipe(
        tap(() => this.authService.logOut(false)),
    ), { dispatch: false });

    constructor(
        private readonly actions$: Actions,
        private readonly authApi: AuthApiService,
        private readonly router: Router,
        private readonly dialog: MatDialog,
        private readonly loginErrorService: LoginErrorNotificationService,
        private readonly authBroadcastChannel: AuthBroadcastChannelService,
    ) {
    }
}
